import { Heading, VisuallyHidden } from "@ariakit/react";
import {
  DownloadLink,
  Failure,
  Success,
  type FailurePayload,
  type RedeemedPledge,
} from "@indietabletop/appkit";
import type { ReactNode } from "react";
import useSWR from "swr";
import { Link, useParams } from "wouter";
import { FailureView } from "../FailureView/FailureView.tsx";
import { LoadingView } from "../LoadingView/LoadingView.tsx";
import coverImageUrl from "../assets/cover.png";
import warbandImageUrl from "../assets/warband.png";
import { client } from "../client.ts";
import { button } from "../common.css.ts";
import { Page, ViewContent } from "../oss/Layout/Layout.tsx";
import { swrResponseToResult } from "../useFetcher.ts";
import * as css from "./style.css.ts";

export function DownloadButton(props: { href: string }) {
  const { href } = props;

  return (
    <DownloadLink href={href} className={button()}>
      Download
    </DownloadLink>
  );
}

export function BookCover(props: { coverImageUrl?: string | null }) {
  return (
    <img
      src={props.coverImageUrl || coverImageUrl}
      alt=""
      className={css.bookCover}
      width={922}
      height={1254}
    />
  );
}

export function DownloadSection(props: {
  action: ReactNode;
  coverImageUrl?: string | null;
}) {
  return (
    <main className={css.downloadSection}>
      <VisuallyHidden>
        <Heading>Download Greathelm</Heading>
      </VisuallyHidden>

      <div className={css.bookContainer}>
        <BookCover coverImageUrl={props.coverImageUrl} />
      </div>

      <div className={css.action}>{props.action}</div>
    </main>
  );
}

function TryAppSection() {
  return (
    <div className={css.hook}>
      <img src={warbandImageUrl} alt="" className={css.hookImage} />
      <p className={css.hookLead}>
        Fancy a warband
        <br /> builder?
      </p>
      <p className={css.hookText}>
        Build the perfect warband for <em>Quick Battles</em>, share it with your
        friends & enemies, and more!
      </p>
      <Link href="~/" className={button()}>
        Explore the app
      </Link>
    </div>
  );
}

async function fetcher(id: string) {
  if (id.startsWith("test")) {
    await new Promise((r) => setTimeout(r, 1000));

    const fakeSuccess = new Success<
      RedeemedPledge & { thumbnailSrc?: string | null }
    >({
      id: "test",
      downloadUrl: "/test.pdf",
      downloadUrlExpiresTs: 0,
      contactSubscribed: false,
      email:
        id === "test-malev" ? "malevolentmc@gmail.com" : "test@example.com",
    });

    return fakeSuccess;
  }

  if (id.startsWith("error404")) {
    return new Failure<FailurePayload>({
      type: "API_ERROR",
      code: 404,
    });
  }

  if (id.startsWith("error403")) {
    return new Failure<FailurePayload>({
      type: "API_ERROR",
      code: 403,
    });
  }

  return client.redeemPledge("greathelm", id);
}

export function RedeemPage() {
  const { id } = useParams<{ id: string }>();
  const result = swrResponseToResult(useSWR(id, fetcher));

  if (result.isPending) {
    return (
      <Page backHref="~/" title="Rewards">
        <LoadingView />
      </Page>
    );
  }

  if (result.isFailure) {
    return (
      <Page backHref="~/" title="Rewards">
        <FailureView failure={result.failure} />
      </Page>
    );
  }

  const pledge = result.value;
  return (
    <Page backHref="~/" title="Rewards">
      <ViewContent inlineVariant="narrow">
        <DownloadSection
          coverImageUrl={pledge.thumbnailSrc}
          action={<DownloadButton href={pledge.downloadUrl} />}
        />
        <TryAppSection />
      </ViewContent>
    </Page>
  );
}
